import {ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormControl, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators} from "@angular/forms";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {debounceTime, distinctUntilChanged, takeUntil} from "rxjs";
import {ToastrService} from "ngx-toastr";
import {NgMultiSelectDropDownModule} from "ng-multiselect-dropdown";
import {TableFilterInputComponent} from "./table-filter-input/table-filter-input.component";
import {AuthenticatedBaseComponent} from '../../authenticated/authenticated-base.component';
import {TableDisplayConfig} from '../../entities/table-display-config.entity';
import {ListItem} from '../../entities/list-item.entity';
import {ContentHeaderService} from '../../services/content-header.service';
import {TitleService} from '../../services/title.service';
import {NgForOf, NgIf, NgTemplateOutlet} from '@angular/common';
import {ReadOnlyStaticComponent} from '../form-inputs/read-only-static/read-only-static.component';
import {Backend} from '@generated-backend-types';
import TableConfig = Backend.App.Resources.Dashboard.Meta.TableConfig;
import TableConfigColumn = Backend.App.Resources.Dashboard.Meta.TableConfigColumn;
import TableConfigColumnTypeEnum = Backend.App.Enums.TableConfigColumnTypeEnum;


@Component({
  selector: 'app-dropdown-table-filter',
  templateUrl: './dropdown-table-filter.component.html',
  imports: [
    ReactiveFormsModule,
    NgMultiSelectDropDownModule,
    FormsModule,
    TableFilterInputComponent,
    NgForOf,
    NgTemplateOutlet,
    NgIf,
    TranslatePipe,
    ReadOnlyStaticComponent,
  ],
  standalone: true
})
export class DropdownTableFilterComponent extends AuthenticatedBaseComponent implements OnInit {

  public test: unknown;

  @Output() public settingsClicked: EventEmitter<void> = new EventEmitter<void>();

  @Output() public tableDisplayConfigChange: EventEmitter<TableDisplayConfig> = new EventEmitter<TableDisplayConfig>();
  public tableConfig?: TableConfig;
  @Input() public globalFilter?: Record<string, string>;
  public columnFilterForm = this.fb.group({});
  public tableColumnOptions: ListItem[] = [];
  public activeFilterTableColumns: TableConfigColumn[] = [];
  public addingFilterFormControl: FormControl = new FormControl();
  protected readonly Validators = Validators;
  protected readonly Infinity = Infinity;

  public constructor(
    protected override cdr: ChangeDetectorRef,
    protected override translate: TranslateService,
    protected override contentHeaderService: ContentHeaderService,
    protected override readonly titleService: TitleService,
    private readonly fb: NonNullableFormBuilder,
    private toastrService: ToastrService,
  ) {
    super(cdr, translate, contentHeaderService, titleService)
  }

  public _tableDisplayConfig?: TableDisplayConfig;

  @Input()
  public set tableDisplayConfig(conf: TableDisplayConfig | undefined) {
    if (!conf) {
      return;
    }
    this.tableConfig = conf.tableConfig;
    this._tableDisplayConfig = JSON.parse(JSON.stringify(conf));

    const values: { [key: string]: string | string[] | null } = {}

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this._tableDisplayConfig.tableConfig?.columns?.map(col => {
      if (col.filterable) {
        let value: string | string[] | null
        if (this._tableDisplayConfig && this._tableDisplayConfig.filter[col.name] || conf.filter[col.name] === '') {
          value = conf.filter[col.name];
        } else {
          value = col.default_filter;
        }
        if (typeof value === 'string' && value?.includes(',')) {
          value = value.split(',');
        }

        values[col.name] = value;

        if (!this.columnFilterForm.get(col.name)) {
          this.columnFilterForm.addControl(col.name, this.getFormControlFor(col), {emitEvent: false});
        }
      }
    })

    this.tableColumnOptions = [];
    // If column is not filterable and not currently an active filter
    this.tableConfig?.columns.filter(col =>
      col.filterable &&
      !(col.name in (this._tableDisplayConfig?.filter ?? {}))
    ).forEach(col => {
      this.tableColumnOptions.push(
        {
          id: col.name,
          text: this.translate.instant('tables.' + this.tableConfig?.id + '.' + col.name),
          isDisabled: false
        }
      );
    });

    this.tableConfig?.columns.filter(col =>
      col.filterable &&
      (col.name in (this._tableDisplayConfig?.filter ?? {}))
    ).forEach(col => {
      const inActiveList = this.activeFilterTableColumns.findIndex(aCol => aCol.name === col.name) >= 0;
      if (
        !inActiveList &&
        this._tableDisplayConfig?.filter[col.name] !== null &&
        this._tableDisplayConfig?.filter[col.name] !== ''
      ) {
        this.activeFilterTableColumns.push(col);
      }
    });

    this.activeFilterTableColumns.forEach((col, i) => {
      const activeFilter = !!this._tableDisplayConfig?.filter[col.name];
      if (
        !activeFilter ||
        (activeFilter && (
          this._tableDisplayConfig?.filter[col.name] === null || this._tableDisplayConfig?.filter[col.name] === ''
        ))
      ) {
        this.activeFilterTableColumns.splice(i, 1)
      }
    })

    this.columnFilterForm.reset(values, {emitEvent: true, onlySelf: false})
    this.columnFilterForm.markAsUntouched();
    this.cdr.markForCheck();
  }

  public resetFilter() {
    this.columnFilterForm.reset();
    this.tableConfig?.columns.forEach((col: TableConfigColumn) => {
      this.columnFilterForm.setControl(col.name, this.getFormControlFor(col));
    })
  }

  public ngOnInit() {
    this.columnFilterForm.valueChanges.pipe(
      debounceTime(500)
    ).subscribe(
      (formValue: Record<string, string[] | string | boolean | undefined | null>) => {

        const oldFilter = JSON.parse(JSON.stringify(this._tableDisplayConfig));

        if (this._tableDisplayConfig) {
          this._tableDisplayConfig.filter = {};
          Object.keys(formValue).forEach((key) => {
            const value = formValue[key];
            if (Array.isArray(value) && this._tableDisplayConfig) {
              if (value.length > 0) {
                this._tableDisplayConfig.filter[key] = value.join(",")
              } else {
                delete this._tableDisplayConfig.filter[key];
              }
            } else if (typeof value === "string" && this._tableDisplayConfig) {
              if (value !== '') {
                this._tableDisplayConfig.filter[key] = value;
              } else {
                delete this._tableDisplayConfig.filter[key];
              }
            } else if (value === null && this._tableDisplayConfig) {
              delete this._tableDisplayConfig.filter[key];
            } else {
              console.error({key, value})
              throw Error('Unhandled Datatype in filter')
            }

          });

          if (!this.areObjectsIdentical(oldFilter, this._tableDisplayConfig) && this.columnFilterForm.valid) {
            this.tableDisplayConfigChange.emit(this._tableDisplayConfig)
          }
          // else {
          //   this.columnFilterForm.markAllAsTouched();
          // }

        }

      }
    )

    this.addingFilterFormControl.valueChanges.pipe(
      takeUntil(this.onDestroy$),
      distinctUntilChanged(),
      debounceTime(200)
    ).subscribe({
      next: (value: ListItem[]) => {
        this.addingFilterFormControl.reset(null, {emitEvent: false});
        if (value && value.length > 0 && value[0]) this.selectFilter(value[0]);
      }
    })

  }

  public selectFilter(selectedItem: ListItem): void {
    const selectedColumn = this.tableConfig?.columns.find(col => {
      return col.name === selectedItem.id;
    })
    if (selectedColumn) {
      const duplicateInActiveFilters = this.activeFilterTableColumns?.includes(selectedColumn)
      if (!duplicateInActiveFilters) {
        this.activeFilterTableColumns.push(selectedColumn);
        const optionIndex = this.tableColumnOptions.findIndex(option => option.id === selectedItem.id)
        if (optionIndex >= 0) {
          this.tableColumnOptions.splice(optionIndex, 1);
        }
      }
    }
    const options = JSON.parse(JSON.stringify(this.tableColumnOptions))
    this.tableColumnOptions = [];
    this.tableColumnOptions = options;
    this.cdr.detectChanges();
  }

  public deselectFilter(selectedItem: TableConfigColumn): void {
    this.columnFilterForm.get(selectedItem.name)?.setValue(selectedItem.default_filter);
    this.activeFilterTableColumns = this.activeFilterTableColumns.filter(item => item.name !== selectedItem.name)
    this.cdr.markForCheck();
  }

  private getFormControlFor(col: TableConfigColumn): FormControl {
    const formControl: FormControl = new FormControl(col.default_filter);

    if (col.required_filter) {
      switch (col.type) {
        case TableConfigColumnTypeEnum.BOOL:
        case TableConfigColumnTypeEnum.ARRAY:
        case TableConfigColumnTypeEnum.STRING:
          formControl.setValidators(col.required_filter ? [Validators.required] : []);
          break;
        default:
          console.error('required filter for column type ' + col.type + ' not implemented');
          this.toastrService.error('required filter for column type ' + col.type + ' not implemented');
      }
    }

    formControl.valueChanges.subscribe(() => formControl.markAsTouched());

    return formControl;
  }

  private areObjectsIdentical(obj1: { [key: string]: unknown }, obj2: { [key: string]: unknown }): boolean {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    // Check if the number of keys is the same
    if (keys1.length !== keys2.length) {
      return false;
    }

    // Check if all keys in obj1 have the same values in obj2
    for (const key of keys1) {
      if (obj1[key] !== obj2[key]) {
        return false;
      }
    }

    return true;
  }
}
